import React, { useState } from 'react';
import { supabase } from './supabase';
import { ArrowLeft, Mail, Eye, EyeOff, Lock, CheckCircle } from 'lucide-react';
import { nameError, emailError } from './validators';

const Login = ({ onBack, onLoginSuccess, initialMode = 'login', inviteData = null, hasInvite = false }) => {
  const [isSignup, setIsSignup] = useState(initialMode === 'signup');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState(inviteData?.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  
  // Email confirmation resend states
  const [signupEmail, setSignupEmail] = useState('');
  const [canResendEmail, setCanResendEmail] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  
  // Forgot password flow states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Trim password to prevent space-only issues
    const trimmedPassword = password.trim();
    if (!trimmedPassword) {
      setError('Please enter your password');
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Explicitly notify App.js of successful login
      if (data?.session?.user && onLoginSuccess) {
        onLoginSuccess(data.session);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    // Validate full name
    const trimmedName = fullName.trim();
    const nameErr = nameError(trimmedName, 'Full name');
    if (nameErr) {
      setError(nameErr);
      setLoading(false);
      return;
    }

    // Validate email format
    const emailErr = emailError(email);
    if (emailErr) {
      setError(emailErr);
      setLoading(false);
      return;
    }

    // Validate password - no leading/trailing spaces and minimum requirements
    const trimmedPassword = password.trim();
    if (trimmedPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }
    if (password !== trimmedPassword) {
      setError('Password cannot contain leading or trailing spaces');
      setLoading(false);
      return;
    }
    if (/^\s+$/.test(password)) {
      setError('Password cannot be only spaces');
      setLoading(false);
      return;
    }

    try {
      console.log('[Signup] Attempting signup for:', email);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin + '/auth/callback',
          data: {
            full_name: trimmedName
          }
        }
      });

      if (error) {
        console.error('[Signup] Error:', error);
        throw error;
      }

      console.log('[Signup] Success:', data);

      // Detect if email already has an account — Supabase returns empty identities array
      if (data?.user && (!data.user.identities || data.user.identities.length === 0)) {
        console.log('[Signup] Existing user detected — empty identities');
        setError('An account with this email already exists. Try logging in instead.');
        setLoading(false);
        return;
      }

      // Mark signup as complete to disable form and clear fields
      setSignupComplete(true);
      setSignupEmail(email);

      // Clear all form fields
      setFullName('');
      setPassword('');
      setAgreedToTerms(false);

      // Check if email confirmation is required
      if (data?.user && !data.session) {
        setMessage('email_confirmation_required');
        setCanResendEmail(true);
        // Start resend timer
        setResendTimer(60);
        const interval = setInterval(() => {
          setResendTimer(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        // Auto-login successful - App.js will handle redirect to CompanySignup
        setMessage('Account created! Setting up your company...');
      }
    } catch (err) {
      console.error('[Signup] Catch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!signupEmail) return;
    
    setLoading(true);
    try {
      console.log('[Signup] Resending confirmation to:', signupEmail);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: signupEmail,
        options: {
          emailRedirectTo: window.location.origin + '/auth/callback'
        }
      });
      
      if (error) throw error;
      
      alert('Confirmation email resent! Check your inbox and spam folder.');
      setResendTimer(60);
      const interval = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError('Failed to resend: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });

      if (error) throw error;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: window.location.origin + '/auth/callback'
        }
      });

      if (error) throw error;
      setMessage('Check your email for the sign-in link!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordClick = () => {
    setForgotPasswordEmail(email);
    setShowForgotPassword(true);
    setError(null);
    setMessage(null);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotPasswordEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail, {
        redirectTo: `${window.location.origin}?password_reset=true`
      });

      if (error) throw error;
      setForgotPasswordSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordSent(false);
    setForgotPasswordEmail('');
    setError(null);
  };

  return (
    <div style={{
      minHeight: '100vh',
      minHeight: '100dvh',
      background: '#0a0a0a',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '1rem',
      paddingTop: 'max(2rem, env(safe-area-inset-top))',
      paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch'
    }}>
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        maxWidth: '450px',
        width: '100%',
        padding: 'clamp(1.5rem, 5vw, 3rem)',
        border: '1px solid #3a3a3a',
        margin: 'auto 0'
      }}>
        {/* Back Button */}
        {onBack && (
          <button
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1.5rem',
              marginLeft: '-0.5rem',
              padding: '0.5rem',
              fontSize: '1rem',
              minHeight: '44px'
            }}
          >
            <ArrowLeft size={20} />
            Back to home
          </button>
        )}

        {/* Invite Banner */}
        {(inviteData || hasInvite) && (
          <div style={{
            background: '#1a2a1a',
            border: '1px solid #4ecca3',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{ color: '#4ecca3', fontWeight: 'bold', fontSize: '1rem', marginBottom: '0.25rem' }}>
              You've been invited to join {inviteData?.companyName ? '' : 'a team'}
            </div>
            {inviteData?.companyName && (
              <div style={{ color: '#e0e0e0', fontSize: '1.1rem', fontWeight: 'bold' }}>
                {inviteData.companyName}
              </div>
            )}
            {inviteData?.role && (
              <div style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.25rem', textTransform: 'capitalize' }}>
                Role: {(inviteData.role || 'member').replace('_', ' ')}
              </div>
            )}
            <div style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.5rem' }}>
              Sign in with Google or your email to join
            </div>
          </div>
        )}

        {/* Logo */}
        <div style={{
          textAlign: 'center',
          marginBottom: '1.5rem'
        }}>
          <img
            src="/WorkVan-logo-dark.png"
            alt="Work Van"
            style={{
              height: '100px',
              width: 'auto',
              marginBottom: '1rem'
            }}
          />
          {!inviteData && !hasInvite && (
            <p style={{ color: '#888', fontSize: '1rem', margin: 0 }}>
              {isSignup ? 'Create your account' : 'Welcome back'}
            </p>
          )}
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div style={{
            background: '#2a1a1a',
            border: '1px solid #e74c3c',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#e74c3c',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        {message && message !== 'email_confirmation_required' && (
          <div style={{
            background: '#1a2a1a',
            border: '1px solid #4ecca3',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#4ecca3',
            fontSize: '0.9rem'
          }}>
            {message}
          </div>
        )}

        {/* Email Confirmation Required Message */}
        {message === 'email_confirmation_required' && (
          <div style={{
            background: '#1a2a1a',
            border: '1px solid #4ecca3',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            color: '#4ecca3',
            fontSize: '0.9rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>
              ✅ Account Created!
            </div>
            <div style={{ marginBottom: '1rem' }}>
              We've sent a confirmation email to:<br />
              <strong style={{ color: '#fff' }}>{signupEmail}</strong>
            </div>
            <div style={{ 
              background: 'rgba(255, 193, 7, 0.1)', 
              border: '1px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
              color: '#ffc107',
              fontSize: '0.85rem'
            }}>
              <strong>📧 Can't find the email?</strong><br />
              • Check your spam/junk folder<br />
              • Look for sender: noreply@workvanapp.com<br />
              • Add noreply@workvanapp.com to your contacts
            </div>
            
            {canResendEmail && (
              <button
                onClick={handleResendConfirmation}
                disabled={loading || resendTimer > 0}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: resendTimer > 0 ? '#2a2a2a' : '#4ecca3',
                  border: 'none',
                  borderRadius: '8px',
                  color: resendTimer > 0 ? '#666' : '#1a1a1a',
                  fontSize: '0.95rem',
                  fontWeight: 'bold',
                  cursor: (loading || resendTimer > 0) ? 'not-allowed' : 'pointer',
                  marginBottom: '0.75rem'
                }}
              >
                {resendTimer > 0 
                  ? `Resend available in ${resendTimer}s` 
                  : loading ? 'Sending...' : '📧 Resend Confirmation Email'}
              </button>
            )}
            
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.5rem' }}>
              Or try signing up with Google for instant access
            </div>
          </div>
        )}

        {/* Google Login */}
        <button
          onClick={handleGoogleLogin}
          disabled={loading}
          style={{
            width: '100%',
            padding: '0.875rem',
            background: '#fff',
            color: '#000',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            opacity: loading ? 0.7 : 1,
            minHeight: '48px',
            transition: 'all 0.15s ease'
          }}
          onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.transform = 'translateY(0)'; }}
          onMouseDown={(e) => { if (!loading) e.currentTarget.style.transform = 'scale(0.98) translateY(0)'; }}
          onMouseUp={(e) => { if (!loading) e.currentTarget.style.transform = 'scale(1) translateY(-1px)'; }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
            <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
          </svg>
          {isSignup ? 'Sign up with Google (instant)' : 'Continue with Google'}
        </button>
        
        {isSignup && (
          <div style={{
            textAlign: 'center',
            fontSize: '0.75rem',
            color: '#888',
            marginTop: '-0.75rem',
            marginBottom: '1rem'
          }}>
            Google signup works immediately - no email confirmation needed
          </div>
        )}

        {/* Magic Link - Login only */}
        {!isSignup && (
          <button
            onClick={handleMagicLink}
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.875rem',
              background: '#2a2a2a',
              color: '#e0e0e0',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              opacity: loading ? 0.7 : 1,
              minHeight: '48px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.background = '#3a3a3a'; e.currentTarget.style.borderColor = '#4a4a4a'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#2a2a2a'; e.currentTarget.style.borderColor = '#3a3a3a'; }}
            onMouseDown={(e) => { if (!loading) e.currentTarget.style.transform = 'scale(0.98)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <Mail size={18} />
            Email me a sign-in link
          </button>
        )}

        {/* Divider */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ flex: 1, height: '1px', background: '#3a3a3a' }} />
          <span style={{ color: '#888', fontSize: '0.85rem' }}>or use {isSignup ? 'email' : 'password'}</span>
          <div style={{ flex: 1, height: '1px', background: '#3a3a3a' }} />
        </div>

        {/* Email/Password Form */}
        <form onSubmit={isSignup ? handleSignup : handleLogin}>
          {isSignup && (
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#e0e0e0',
                fontSize: '0.9rem',
                fontWeight: '600'
              }}>
                Full Name
              </label>
              <input
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={signupComplete}
                placeholder="John Smith"
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: '#2a2a2a',
                  border: '1px solid #3a3a3a',
                  borderRadius: '8px',
                  color: '#e0e0e0',
                  fontSize: '16px',
                  opacity: signupComplete ? 0.5 : 1
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#e0e0e0',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={signupComplete}
              placeholder="you@example.com"
              style={{
                width: '100%',
                padding: '0.875rem',
                background: '#2a2a2a',
                border: '1px solid #3a3a3a',
                borderRadius: '8px',
                color: '#e0e0e0',
                fontSize: '16px',
                opacity: signupComplete ? 0.5 : 1
              }}
            />
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#e0e0e0',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={signupComplete}
                placeholder="••••••••"
                minLength={6}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  paddingRight: '3rem',
                  background: '#2a2a2a',
                  border: '1px solid #3a3a3a',
                  borderRadius: '8px',
                  color: '#e0e0e0',
                  fontSize: '16px',
                  opacity: signupComplete ? 0.5 : 1
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {isSignup ? (
              <div style={{
                marginTop: '0.5rem',
                fontSize: '0.75rem',
                color: '#888'
              }}>
                Must be at least 6 characters
              </div>
            ) : (
              <button
                type="button"
                onClick={handleForgotPasswordClick}
                disabled={loading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#F05A28',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  padding: '0.5rem 0',
                  minHeight: '36px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.color = '#ff8555'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#F05A28'; }}
              >
                Forgot password?
              </button>
            )}
          </div>

          {/* Terms Agreement Checkbox - Only for Signup */}
          {isSignup && (
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                cursor: 'pointer'
              }}>
                <div
                  onClick={() => setAgreedToTerms(!agreedToTerms)}
                  style={{
                    width: '20px',
                    height: '20px',
                    minWidth: '20px',
                    marginTop: '2px',
                    border: `2px solid ${agreedToTerms ? '#F05A28' : '#666'}`,
                    borderRadius: '4px',
                    background: agreedToTerms ? '#F05A28' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {agreedToTerms && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <span style={{ color: '#aaa', fontSize: '0.85rem', lineHeight: '1.5' }}>
                  I agree to the{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#F05A28', textDecoration: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms & Conditions
                  </a>
                  {' '}and{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#F05A28', textDecoration: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>
                </span>
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || signupComplete || (isSignup && !agreedToTerms)}
            style={{
              width: '100%',
              padding: '0.875rem',
              background: signupComplete ? '#4ecca3' : '#F05A28',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: (loading || signupComplete || (isSignup && !agreedToTerms)) ? 'not-allowed' : 'pointer',
              opacity: (loading || signupComplete || (isSignup && !agreedToTerms)) ? 0.7 : 1,
              minHeight: '48px',
              transition: 'all 0.15s ease',
              boxShadow: signupComplete ? 'none' : '0 4px 14px rgba(240, 90, 40, 0.3)'
            }}
            onMouseEnter={(e) => { 
              if (!loading && !signupComplete) { 
                e.currentTarget.style.background = '#ff8555'; 
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(240, 90, 40, 0.4)';
              } 
            }}
            onMouseLeave={(e) => { 
              e.currentTarget.style.background = signupComplete ? '#4ecca3' : '#F05A28'; 
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = signupComplete ? 'none' : '0 4px 14px rgba(240, 90, 40, 0.3)';
            }}
            onMouseDown={(e) => { if (!loading && !signupComplete) e.currentTarget.style.transform = 'scale(0.98) translateY(0)'; }}
            onMouseUp={(e) => { if (!loading && !signupComplete) e.currentTarget.style.transform = 'scale(1) translateY(-1px)'; }}
          >
            {signupComplete ? 'Account created - check your email' : loading ? (isSignup ? 'Creating account...' : 'Signing in...') : (isSignup ? 'Create account' : 'Sign in')}
          </button>
        </form>

        {/* Toggle Login/Signup */}
        <div style={{
          marginTop: '1.5rem',
          textAlign: 'center',
          color: '#888',
          fontSize: '0.9rem'
        }}>
          {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button
            onClick={() => {
              setIsSignup(!isSignup);
              setError(null);
              setMessage(null);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#F05A28',
              cursor: 'pointer',
              fontSize: '0.9rem',
              textDecoration: 'underline',
              fontWeight: 'bold',
              minHeight: '44px'
            }}
          >
            {isSignup ? 'Sign in' : 'Sign up'}
          </button>
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            zIndex: 1000
          }}>
            <div style={{
              background: '#1a1a1a',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '100%',
              padding: '2rem',
              border: '1px solid #3a3a3a',
              textAlign: 'center'
            }}>
              {!forgotPasswordSent ? (
                <>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'rgba(240, 90, 40, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem'
                  }}>
                    <Lock size={32} color="#F05A28" />
                  </div>
                  <h2 style={{
                    color: '#e0e0e0',
                    margin: '0 0 0.5rem',
                    fontSize: '1.5rem'
                  }}>
                    Reset your password
                  </h2>
                  <p style={{
                    color: '#888',
                    margin: '0 0 1.5rem',
                    fontSize: '0.95rem'
                  }}>
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                  {error && (
                    <div style={{
                      background: '#2a1a1a',
                      border: '1px solid #e74c3c',
                      borderRadius: '8px',
                      padding: '0.75rem',
                      marginBottom: '1rem',
                      color: '#e74c3c',
                      fontSize: '0.9rem'
                    }}>
                      {error}
                    </div>
                  )}
                  <form onSubmit={handleForgotPasswordSubmit}>
                    <input
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        background: '#2a2a2a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '8px',
                        color: '#e0e0e0',
                        fontSize: '1rem',
                        marginBottom: '1rem'
                      }}
                    />
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={handleCloseForgotPassword}
                        disabled={loading}
                        style={{
                          flex: 1,
                          padding: '0.875rem',
                          background: '#2a2a2a',
                          color: '#e0e0e0',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          fontWeight: '600',
                          cursor: loading ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        style={{
                          flex: 2,
                          padding: '0.875rem',
                          background: '#F05A28',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          opacity: loading ? 0.7 : 1
                        }}
                      >
                        {loading ? 'Sending...' : 'Send reset link'}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'rgba(78, 204, 163, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem'
                  }}>
                    <CheckCircle size={32} color="#4ecca3" />
                  </div>
                  <h2 style={{
                    color: '#e0e0e0',
                    margin: '0 0 0.5rem',
                    fontSize: '1.5rem'
                  }}>
                    Check your email
                  </h2>
                  <p style={{
                    color: '#888',
                    margin: '0 0 1.5rem',
                    fontSize: '0.95rem'
                  }}>
                    We've sent a password reset link to:<br />
                    <strong style={{ color: '#e0e0e0' }}>{forgotPasswordEmail}</strong>
                  </p>
                  <p style={{
                    color: '#666',
                    margin: '0 0 1.5rem',
                    fontSize: '0.85rem'
                  }}>
                    Click the link in the email to set a new password. If you don't see it, check your spam folder.
                  </p>
                  <button
                    onClick={handleCloseForgotPassword}
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      background: '#F05A28',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Back to sign in
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
